import type {
    SiteBuilderBlock,
    SiteBuilderContentJson,
    SiteBuilderLeadForm,
} from "./siteBuilderTypes";
import { siteBuilderLeadFieldKey } from "./siteBuilderLeadRuntime";
export {
    siteBuilderGeneratedFormKey,
    withRegeneratedSiteBuilderFormIdentity,
} from "./siteBlockIdentity";

export type SiteBuilderFormConfig = {
    formKey: string | null;
    submitLabel: string | null;
    successMessage: string | null;
};

export type SiteBuilderAuthoredFormField = {
    block: SiteBuilderBlock;
    key: string;
    label: string | null;
    options: string[] | null;
    placeholder: string | null;
    rows: number | null;
};

export type SiteBuilderPublishedFormItems = {
    hasSubmitButton: boolean;
    items: SiteBuilderBlock[];
};

function positiveInteger(value: unknown): number | null {
    const parsed = typeof value === "number"
        ? value
        : typeof value === "string"
            ? Number(value)
            : Number.NaN;

    if (!Number.isFinite(parsed) || parsed < 1) {
        return null;
    }

    return Math.trunc(parsed);
}

function textValue(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();

    return trimmed === "" ? null : trimmed;
}

function optionItems(value: unknown): string[] | null {
    const values = Array.isArray(value)
        ? value
        : typeof value === "string"
            ? value.split(/\r?\n|,/u)
            : [];
    const normalized = values.reduce<string[]>((items, item) => {
        const value = textValue(item);

        if (value && !items.includes(value)) {
            items.push(value);
        }

        return items;
    }, []);

    return normalized.length > 0 ? normalized : null;
}

function formFieldOptions(value: unknown): string[] {
    return Array.isArray(value)
        ? value.filter((option): option is string => typeof option === "string")
        : [];
}

function normalizedFormField(
    field: SiteBuilderLeadForm["fields"][number],
): SiteBuilderLeadForm["fields"][number] {
    return {
        ...field,
        options: formFieldOptions(field.options),
    };
}

function nestedBlockLists(block: SiteBuilderBlock): SiteBuilderBlock[][] {
    return [
        ...Object.values(block.slots ?? {}),
        ...(Array.isArray(block.children) ? [block.children] : []),
    ];
}

/**
 * Mirrors backend builder-form traversal and key generation. The block remains
 * attached so template renderers can preserve its localized presentation and
 * layout while the persisted Form stays the source of validation truth.
 */
export function siteBuilderAuthoredFormFields(
    formBlock: SiteBuilderBlock,
): SiteBuilderAuthoredFormField[] {
    const fields: SiteBuilderAuthoredFormField[] = [];
    const uniqueKeys = new Set<string>();
    let position = 0;

    function visit(blocks: readonly SiteBuilderBlock[]): void {
        blocks.forEach((block) => {
            if (block.type === "field") {
                const content = block.content ?? {};
                const options = block.options ?? {};
                const label = textValue(content.label);
                const rawKey = textValue(content.name)
                    ?? textValue(content.key)
                    ?? label
                    ?? "";
                const key = siteBuilderLeadFieldKey(rawKey, position);

                fields.push({
                    block,
                    key,
                    label,
                    options: optionItems(
                        content.choices
                        ?? content.options
                        ?? content.items
                        ?? options.choices,
                    ),
                    placeholder: textValue(content.placeholder),
                    rows: positiveInteger(options.rows),
                });

                if (!uniqueKeys.has(key)) {
                    uniqueKeys.add(key);
                    position += 1;
                }
            }

            if (block.type === "form") {
                return;
            }

            nestedBlockLists(block).forEach(visit);
        });
    }

    nestedBlockLists(formBlock).forEach(visit);

    return fields;
}

/**
 * Applies localized builder presentation only to fields that still exist in
 * the persisted Form. Removed/unknown and duplicate authored fields never
 * become public controls; fields added in Forms admin remain available.
 */
export function siteBuilderFormWithAuthoredPresentation(
    form: SiteBuilderLeadForm | null | undefined,
    formBlock: SiteBuilderBlock,
): SiteBuilderLeadForm | null {
    if (!form) {
        return null;
    }

    const normalizedFields = form.fields.map(normalizedFormField);
    const schemaByKey = new Map(normalizedFields.map((field) => [field.key, field]));
    const consumedKeys = new Set<string>();
    const authoredFields = siteBuilderAuthoredFormFields(formBlock).flatMap((authored) => {
        const schema = schemaByKey.get(authored.key);

        if (!schema || consumedKeys.has(authored.key)) {
            return [];
        }

        consumedKeys.add(authored.key);

        return [{
            ...schema,
            label: authored.label ?? schema.label,
            options: authored.options ?? schema.options,
            placeholder: authored.placeholder ?? schema.placeholder,
            rows: authored.rows ?? schema.rows,
        }];
    });
    const adminFields = normalizedFields.filter((field) => !consumedKeys.has(field.key));

    return {
        ...form,
        fields: [...authoredFields, ...adminFields],
    };
}

function builderInputType(fieldType: string): string {
    if (fieldType === "textarea") return "longText";
    if (fieldType === "select") return "selection";
    if (fieldType === "tel" || fieldType === "phone") return "telephone";
    if (fieldType === "datetime-local") return "datetime";

    return fieldType || "text";
}

function schemaFieldBlock(
    field: SiteBuilderLeadForm["fields"][number],
): SiteBuilderBlock {
    const options = formFieldOptions(field.options);

    return {
        content: {
            choices: options.join("\n"),
            defaultValue: "",
            label: field.label,
            name: field.key,
            placeholder: field.placeholder ?? "",
        },
        options: {
            fieldInputType: builderInputType(field.type),
            requirement: field.required ? "required" : "none",
            ...(field.rows ? { rows: field.rows } : {}),
            showDescription: false,
        },
        type: "field",
        uuid: field.uuid,
    };
}

function hasNestedSubmitButton(blocks: readonly SiteBuilderBlock[]): boolean {
    return blocks.some((block) => (
        block.type === "submitButton"
        || Object.values(block.slots ?? {}).some(hasNestedSubmitButton)
        || hasNestedSubmitButton(block.children ?? [])
    ));
}

/**
 * Keeps the authored form tree as the public presentation while reconciling
 * every actual control with the persisted Form schema. Unknown and duplicate
 * authored fields stay out of the public form; Form-admin fields that have no
 * authored block are appended as neutral controls.
 */
export function siteBuilderPublishedFormItems(
    form: SiteBuilderLeadForm | null | undefined,
    formBlock: SiteBuilderBlock,
): SiteBuilderPublishedFormItems {
    const sourceItems = formBlock.slots?.items ?? [];

    if (!form) {
        return {
            hasSubmitButton: hasNestedSubmitButton(sourceItems),
            items: sourceItems,
        };
    }

    const normalizedFields = form.fields.map(normalizedFormField);
    const fieldsByKey = new Map(normalizedFields.map((field) => [field.key, field]));
    const authoredByBlock = new Map(
        siteBuilderAuthoredFormFields(formBlock).map((field) => [field.block, field]),
    );
    const consumedKeys = new Set<string>();

    const reconcile = (block: SiteBuilderBlock): SiteBuilderBlock | null => {
        if (block.type === "form") {
            return null;
        }

        if (block.type === "field") {
            const authored = authoredByBlock.get(block);
            const field = authored ? fieldsByKey.get(authored.key) : undefined;

            if (!authored || !field || consumedKeys.has(authored.key)) {
                return null;
            }

            consumedKeys.add(authored.key);

            return {
                ...block,
                content: {
                    ...block.content,
                    ...(block.content?.choices === undefined && field.options.length > 0
                        ? { choices: field.options.join("\n") }
                        : {}),
                    ...(block.content?.label === undefined ? { label: field.label } : {}),
                    name: field.key,
                    ...(block.content?.placeholder === undefined
                        ? { placeholder: field.placeholder ?? "" }
                        : {}),
                },
                options: {
                    ...block.options,
                    fieldInputType: builderInputType(field.type),
                    requirement: field.required ? "required" : "none",
                    ...(block.options?.rows === undefined && field.rows
                        ? { rows: field.rows }
                        : {}),
                },
            };
        }

        const slots = Object.fromEntries(
            Object.entries(block.slots ?? {}).map(([slot, children]) => [
                slot,
                children.flatMap((child) => {
                    const reconciled = reconcile(child);

                    return reconciled ? [reconciled] : [];
                }),
            ]),
        );
        const children = (block.children ?? []).flatMap((child) => {
            const reconciled = reconcile(child);

            return reconciled ? [reconciled] : [];
        });

        return {
            ...block,
            ...(block.slots === undefined ? {} : { slots }),
            ...(block.children === undefined ? {} : { children }),
        };
    };

    const items = sourceItems.flatMap((block) => {
        const reconciled = reconcile(block);

        return reconciled ? [reconciled] : [];
    });
    const missingFields = normalizedFields
        .filter((field) => !consumedKeys.has(field.key))
        .map(schemaFieldBlock);
    const submitIndex = items.findIndex((block) => block.type === "submitButton");
    const withMissingFields = submitIndex < 0
        ? [...items, ...missingFields]
        : [
            ...items.slice(0, submitIndex),
            ...missingFields,
            ...items.slice(submitIndex),
        ];

    return {
        hasSubmitButton: hasNestedSubmitButton(withMissingFields),
        items: withMissingFields,
    };
}

export function siteBuilderFormConfig(block: SiteBuilderBlock): SiteBuilderFormConfig {
    return {
        formKey: textValue(block.content?.formKey)
            ?? textValue(block.content?.purpose),
        submitLabel: textValue(block.content?.submitLabel),
        successMessage: textValue(block.content?.successMessage),
    };
}

function documentBlocks(contentJson: SiteBuilderContentJson | null | undefined): SiteBuilderBlock[] {
    return Array.isArray(contentJson?.blocks) ? contentJson.blocks : [];
}

export function siteBuilderFormKeys(
    ...documents: Array<SiteBuilderContentJson | null | undefined>
): string[] {
    const keys: string[] = [];

    function visit(blocks: SiteBuilderBlock[]) {
        for (const block of blocks) {
            if (block.type === "form") {
                const key = siteBuilderFormConfig(block).formKey;

                if (key && !keys.includes(key)) {
                    keys.push(key);
                }
            }

            Object.values(block.slots ?? {}).forEach(visit);
            visit(block.children ?? []);
        }
    }

    documents.forEach((document) => visit(documentBlocks(document)));

    return keys;
}

export function siteBuilderFormForKey<T extends { purpose: string }>(
    forms: readonly T[],
    formKey: string | null | undefined,
): T | null {
    if (formKey) {
        return forms.find((form) => form.purpose === formKey) ?? null;
    }

    return forms[0] ?? null;
}

export function siteBuilderUsesLegacyFormPreview(
    editorMode: boolean,
    itemCount: number,
): boolean {
    return editorMode && itemCount > 0;
}

export function siteBuilderUsesAuthoredFormPreview(
    editorMode: boolean,
    itemCount: number,
): boolean {
    return editorMode && itemCount > 0;
}
